import { motion } from "framer-motion";
import { AgentAvatar } from "./AgentAvatar";
import { Markdown } from "./markdown";
import { ThinkingDropdown } from "./messages/ThinkingDropdown";
import { RecommendationCard } from "./messages/RecommendationCard";
import { ComparisonBlock } from "./messages/ComparisonBlock";
import { TruthAuditBlock } from "./messages/TruthAuditBlock";
import { OcrResultCard } from "./messages/OcrResultCard";
import { StepFlow } from "./messages/StepFlow";
import { CitationsRow } from "./messages/CitationsRow";
import { AnswerMeta } from "./messages/AnswerMeta";
import { AnswerFeedback } from "./messages/AnswerFeedback";
import { SpeakButton } from "./messages/SpeakButton";
import type { Message } from "./types";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Beyond Sure — single message row.
 *
 * Claude-fidelity choices:
 *   - Assistant turns: avatar on the left, body in a quiet container
 *     that doesn't draw the eye (calm reading surface).
 *   - User turns: right-aligned, soft brand-mint container.
 *   - Streaming caret: a blinking dot at the end of the body while the
 *     assistant is still emitting tokens.
 *   - System turns: small, centered, muted (used for "I read your
 *     policy" hand-offs in Phase 2 — Phase 1 falls through to a quiet
 *     pill).
 *
 * Markdown is rendered by the local `markdown.tsx` renderer; rich
 * blocks (recommendation/comparison/etc.) land in Phase 2 via the
 * `blocks` field on the Message.
 */

interface MessageBubbleProps {
  message: Message;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  if (message.role === "system") {
    return (
      <div className="flex justify-center" data-testid="message-system">
        <div className="rounded-full px-3 py-1 bg-[var(--color-ink-08)] text-[12px] text-[var(--color-ink-60)]">
          {message.content}
        </div>
      </div>
    );
  }

  if (message.role === "user") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="flex flex-col items-end gap-1.5"
        data-testid="message-user"
      >
        {/*
          Attached file (Phase 3): rendered as a small chip ABOVE the
          message bubble so the user sees what they sent without it
          competing with their own text. Clicking the chip is reserved
          for Phase 3+ (preview the PDF inline).
        */}
        {message.attachment ? (
          <div
            data-testid="user-attachment"
            className={[
              "inline-flex items-center gap-2 max-w-[78%]",
              "rounded-[10px] px-3 py-2",
              "bg-[var(--color-paper)] border border-[var(--color-ink-08)]",
              "text-[12.5px] text-[var(--color-ink)]",
            ].join(" ")}
          >
            <span
              className="inline-flex items-center justify-center h-7 w-7 rounded-[6px] shrink-0"
              style={{ backgroundColor: "var(--color-brand-soft)" }}
              aria-hidden
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 3h7l5 5v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
                  stroke="var(--color-brand-deep)"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 3v5h5"
                  stroke="var(--color-brand-deep)"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-medium truncate max-w-[220px]">
                {message.attachment.fileName}
              </span>
              <span className="text-[11px] text-[var(--color-ink-60)]">
                {formatBytes(message.attachment.sizeBytes)}
              </span>
            </span>
          </div>
        ) : null}

        {/* The text bubble itself is suppressed when the message is
            only an attachment — the chip stands on its own. */}
        {message.content.trim() ? (
          <div
            className={[
              "max-w-[78%] rounded-[18px] rounded-tr-[6px]",
              "bg-[var(--color-brand-soft)] text-[var(--color-ink)]",
              "px-4 py-2.5 text-[15px] leading-[1.55]",
            ].join(" ")}
          >
            <span className="whitespace-pre-wrap">{message.content}</span>
          </div>
        ) : null}
      </motion.div>
    );
  }

  // Assistant turn.
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="flex gap-3"
      data-testid="message-assistant"
    >
      <div className="pt-0.5">
        <AgentAvatar size={28} pulse={message.streaming} />
      </div>
      <div className="flex-1 min-w-0">
        {/*
          Optional "Thinking…" dropdown (Phase 2 surfaces this for any
          message whose `thinking` field is set). It collapses by default.
        */}
        {message.thinking ? (
          <div className="mb-2">
            <ThinkingDropdown summary={message.thinking} />
          </div>
        ) : null}

        {/* Body — markdown rendered. */}
        <div className="text-[var(--color-ink)]">
          <Markdown source={message.content} />
          {message.streaming ? (
            <span
              aria-hidden
              className="inline-block w-[7px] h-[16px] -mb-[2px] ml-[2px] bg-[var(--color-brand-deep)] bsure-pulse rounded-[1px]"
              data-testid="streaming-caret"
            />
          ) : null}
        </div>

        {/* Rich blocks (recommendation, comparison, truth-audit, OCR).
            Rendered AFTER the markdown body so the assistant's prose
            sets up the reader for the structured surface below. */}
        {message.blocks && message.blocks.length > 0 ? (
          <div className="mt-3 flex flex-col gap-3">
            {message.blocks.map((block, i) => {
              switch (block.kind) {
                case "recommendation":
                  return <RecommendationCard key={i} data={block.data} />;
                case "comparison":
                  return <ComparisonBlock key={i} data={block.data} />;
                case "truth-audit":
                  return <TruthAuditBlock key={i} data={block.data} />;
                case "ocr-result":
                  return <OcrResultCard key={i} data={block.data} />;
                case "steps":
                  return <StepFlow key={i} data={block.data} />;
                default:
                  return null;
              }
            })}
          </div>
        ) : null}

        {/* RAG source chips — now fed by the live answer envelope (L04a maps citations). */}
        {message.citations && message.citations.length > 0 ? (
          <CitationsRow citations={message.citations} />
        ) : null}

        {/* BD3.1: free voice output — "Listen" reads the answer aloud via the
            Web Speech API (lib/tts.ts). Hidden while streaming and on browsers
            without speechSynthesis (the button renders null, no dead control). */}
        {!message.streaming && message.content.trim() ? (
          <div className="mt-2">
            <SpeakButton text={message.content} />
          </div>
        ) : null}

        {/* L04: answer meta — forced-Strict badge, mode chip, confidence meter, honest gate badge.
            Renders nothing for messages without envelope signal (mock/system turns). */}
        {!message.streaming ? <AnswerMeta message={message} /> : null}

        {/* L04: thumbs up/down → capture (training flywheel). Only on REAL answers (available===true),
            never on the no-key gate or mock turns. */}
        {!message.streaming && message.available === true ? (
          <AnswerFeedback messageId={message.id} />
        ) : null}
      </div>
    </motion.div>
  );
}

export default MessageBubble;
